import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode, Suspense, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, Outlet, useNavigation } from 'react-router-dom';
import styled from 'styled-components';

import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { DisplayS, LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { ANY_SPORT_PLAY } from '@sorare/core/src/constants/__generated__/routes';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useQueryLoaderResult } from '@sorare/core/src/hooks/useQueryLoaderResult';
import { glossary } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import FixtureSelector from 'components/fixture/FixtureSelector';
import { FixtureTeamsOverview } from 'components/fixture/FixtureTeamsOverview';
import LineupStyleSwitch from 'components/lineup/LineupStyleSwitch';
import { LastFixtureStory } from 'components/so5/LastFixtureStory';
import {
  getFixtureQueryVariables,
  getLeaguesWithContenders,
  isFixtureOpened,
} from 'lib/so5';

import { RightButton } from './RightButton';
import {
  FixtureLayoutQuery,
  FixtureLayoutQueryVariables,
} from './__generated__/index.graphql';

const FIXTURE_LAYOUT_QUERY = gql`
  query FixtureLayoutQuery(
    $type: So5State
    $slug: String
    $eventType: So5FixtureEvent
    $sport: Sport!
  ) {
    so5 {
      ongoingSo5Fixture: so5Fixture(
        type: MY_ONGOING
        eventType: $eventType
        sport: $sport
      ) {
        slug
        ...RightButton_ongoingSo5Fixture
      }
      nextSo5Fixture: so5Fixture(
        type: UPCOMING
        eventType: $eventType
        sport: $sport
      ) {
        slug
      }
      so5Fixture(
        type: $type
        eventType: $eventType
        slug: $slug
        sport: $sport
      ) {
        slug
        displayName
        endDate
        type
        canCompose
        so5LeaderboardGroups {
          slug
          ...getLeaguesWithContenders_so5LeaderboardGroupInterface
        }
        ...FixtureSelector_so5Fixture
        ...FixtureTeamsOverview_so5Fixture
        ...RightButton_so5Fixture
        ...isFixtureOpened_so5Fixture
      }
    }
  }
  ${FixtureSelector.fragments.so5Fixture}
  ${FixtureTeamsOverview.fragments.so5Fixture}
  ${isFixtureOpened.fragments.so5Fixture}
  ${RightButton.fragments.ongoingSo5Fixture}
  ${RightButton.fragments.so5Fixture}
  ${getLeaguesWithContenders.fragments.so5LeadearboardGroupInterface}
` as TypedDocumentNode<FixtureLayoutQuery, FixtureLayoutQueryVariables>;

const StyledVertical = styled(Vertical)`
  align-items: start;
`;
const Background = styled.div`
  background-size: cover;
  background-attachment: fixed;
  min-height: calc(var(--100vh) - var(--current-stack-height));
  --current-background-color: transparent;
`;

const Root = styled(Vertical)`
  padding-top: var(--triple-unit);
  padding-bottom: var(--triple-unit);

  @media ${laptopAndAbove} {
    padding-top: 0;
  }
`;
const LineupStyleSwitcher = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  z-index: 1;
  top: var(--half-unit);
  right: 0;
  left: 0;
`;
const Header = styled(FullWidthContainer)`
  display: flex;
  justify-content: space-between;
  z-index: 1;
  padding: var(--triple-unit) var(--container-padding);
  align-items: center;
  min-height: calc(
    5 * var(--unit)
  ); /** height of the buttons on the side to avoid layout shift */

  > * {
    flex: 1;

    &:last-child > * {
      width: min-content;
      margin-left: auto;
    }
  }

  ${LineupStyleSwitcher} {
    position: static;
    justify-content: flex-end;
    margin-top: var(--double-unit);
  }
`;
const FixtureSelectorContainer = styled.div`
  margin: auto;
  position: relative;
  max-width: min(100%, 430px);
`;

const getTitleFromEventType = (type: So5FixtureEvent) => {
  switch (type) {
    case So5FixtureEvent.DAILY:
      return glossary.dailyHoops;
    case So5FixtureEvent.CLASSIC:
    default:
      return glossary.pro;
  }
};

export const loaderFn = <
  Params extends {
    eventType?: string;
    fixture?: string;
  },
>() =>
  queryLoader<Params>(
    FIXTURE_LAYOUT_QUERY,
    ({ sport, eventType, fixture }) => {
      return {
        ...getFixtureQueryVariables(fixture),
        sport,
        eventType: eventType?.toUpperCase(),
      };
    },
    { preventTransitionBeforeLoaded: true }
  );

type Props = {
  sport: Sport;
  withOnboarding?: boolean;
  eventType: So5FixtureEvent;
  fixture?: string;
  background?: string;
  displayName?: ReactNode;
  withLineupStyleSwitch: boolean;
  showDiscoverNewCompetitions?: boolean;
};
export const FixtureLayout = ({
  fixture,
  eventType,
  background,
  displayName,
  sport,
  withOnboarding,
  withLineupStyleSwitch,
  showDiscoverNewCompetitions = true,
}: Props) => {
  const navigation = useNavigation();
  const isNavigating = Boolean(navigation.location);
  const { generateSportContextPath } = useSportContext();
  const data = useQueryLoaderResult<FixtureLayoutQuery>();
  const [fixtureLayoutTitle, setFixtureLayoutTitle] = useState<string>();
  const isLaptopAndAbove = useIsLaptop();

  const { so5 } = data;
  const { so5Fixture, ongoingSo5Fixture, nextSo5Fixture } = so5;

  if (!so5Fixture) {
    if (fixture === 'ongoing' && !ongoingSo5Fixture) {
      return (
        <Navigate
          to={generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
            params: {
              fixture: nextSo5Fixture?.slug || 'live',
              eventType: eventType.toLowerCase(),
            },
          })}
          replace
        />
      );
    }
    return null;
  }

  if (!fixtureLayoutTitle && so5Fixture.displayName) {
    setFixtureLayoutTitle(so5Fixture.displayName);
  }

  const isUpcoming = isFixtureOpened(so5Fixture);
  const { so5LeaderboardGroupsWithContenders } = getLeaguesWithContenders(
    so5Fixture.so5LeaderboardGroups,
    isUpcoming
  );

  const overview = so5LeaderboardGroupsWithContenders.length > 0 && (
    <FixtureTeamsOverview so5Fixture={so5Fixture} />
  );

  const lineupStyleSwitch = withLineupStyleSwitch && (
    <LineupStyleSwitcher>
      <LineupStyleSwitch sport={sport} />
    </LineupStyleSwitcher>
  );

  return (
    <ConditionalWrapper
      Wrapper={Background}
      wrap={!!background}
      props={{ style: { backgroundImage: background } }}
    >
      {!isLaptopAndAbove && (
        <>
          <Portal id="page-header-right-button">{overview}</Portal>
          <Portal id="above-bottom-bar-portal">
            <RightButton
              so5Fixture={so5Fixture}
              sport={sport}
              showDiscoverNewCompetitions={showDiscoverNewCompetitions}
              ongoingSo5Fixture={ongoingSo5Fixture}
            />
          </Portal>
        </>
      )}
      <PageHeader
        customTitle={
          <StyledVertical gap={0}>
            <LabelL as="h1" bold>
              <FormattedMessage {...getTitleFromEventType(so5Fixture.type)} />
            </LabelL>
            {!isLaptopAndAbove && (
              <LabelM as="p" color="var(--c-nd-600)">
                {fixtureLayoutTitle}
              </LabelM>
            )}
          </StyledVertical>
        }
        backTo={generateSportContextPath(ANY_SPORT_PLAY)}
      />
      <ContentWithStickyHeader
        header={
          <Header>
            <div>{overview}</div>
            {!displayName && (
              <DisplayS as="p" className="text-center">
                {fixtureLayoutTitle}
              </DisplayS>
            )}
            <div>
              <RightButton
                sport={sport}
                so5Fixture={so5Fixture}
                showDiscoverNewCompetitions={showDiscoverNewCompetitions}
                ongoingSo5Fixture={ongoingSo5Fixture}
              />
              {isLaptopAndAbove && lineupStyleSwitch}
            </div>
          </Header>
        }
        hideHeader={!isLaptopAndAbove}
      >
        <Root gap={isLaptopAndAbove ? 5 : 2}>
          {displayName}
          <FullWidthContainer>
            <OnboardingHighlightWrapper
              wrap={withOnboarding}
              highlightName="FixtureSelectorContainer"
            >
              <FixtureSelectorContainer>
                <FixtureSelector
                  withMask
                  currentFixture={so5Fixture}
                  onFixtureChanged={setFixtureLayoutTitle}
                />
              </FixtureSelectorContainer>
            </OnboardingHighlightWrapper>
            {!isLaptopAndAbove && lineupStyleSwitch}
          </FullWidthContainer>
          <Suspense fallback={<LoadingIndicator />}>
            {isNavigating ? <LoadingIndicator /> : <Outlet />}
          </Suspense>
        </Root>
      </ContentWithStickyHeader>
      <LastFixtureStory sport={sport} />
    </ConditionalWrapper>
  );
};
